import React, { useMemo, useState, useRef, useEffect } from "react";
import classNames from "classnames";
import styles from "./styles.module.scss";
import countryList from "react-select-country-list";
import DownArrow from "../../SVGcomponents/DownArrow";
import { SearchOutlined } from "@ant-design/icons";
import { filterCountriesBySearch } from "../../../helpers/countrySearch";
import { groupCountriesByRegion } from "../../../helpers/country-regions";

interface CountryOption {
  label: string;
  value: string;
  icon: string;
}

interface SimpleCountrySelectProps {
  value?: CountryOption | null;
  onChange: (option: CountryOption) => void;
  placeholder?: string;
  className?: string;
  height?: number;
  disabled?: boolean;
}

export const getCountriesWithIcons = () => {
  const countries = countryList().getData();
  return countries.map((country) => {
    if (country.value === "VN" && country.label === "Viet Nam") {
      return {
        ...country,
        label: "Vietnam",
        icon: `https://hatscripts.github.io/circle-flags/flags/${country.value.toLowerCase()}.svg`,
      };
    }
    return {
      ...country,
      icon: `https://hatscripts.github.io/circle-flags/flags/${country.value.toLowerCase()}.svg`,
    };
  });
};

const SimpleCountrySelect = ({
  value,
  onChange,
  placeholder = "Select country",
  className,
  height = 44,
  disabled = false,
}: SimpleCountrySelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const options = useMemo(() => getCountriesWithIcons(), []);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  const filteredOptions = useMemo(
    () => filterCountriesBySearch(options, searchTerm),
    [options, searchTerm],
  );

  // Group by region in the default (non-search) view so Afghanistan no longer
  // leads the list; keep the flat relevance order while searching.
  const optionGroups = useMemo(
    () =>
      searchTerm.trim()
        ? null
        : groupCountriesByRegion(filteredOptions as CountryOption[]),
    [filteredOptions, searchTerm],
  );

  const handleSelect = (option: CountryOption) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div
      className={styles.container}
      style={{ height: `${height}px` }}
      ref={containerRef}
    >
      <div
        className={classNames(
          styles.selectWrapper,
          className,
          { [styles.open]: isOpen },
          { [styles.disabled]: disabled },
        )}
      >
        {!isOpen && value ? (
          <>
            <img src={value.icon} alt={value.label} className={styles.flag} />
            <span className={styles.label} onClick={handleInputClick}>
              {value.label} ({value.value})
            </span>
          </>
        ) : (
          <>
            <SearchOutlined className={styles.searchIconInline} />
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInputInline}
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleInputChange}
              onClick={handleInputClick}
              disabled={disabled}
            />
          </>
        )}
        <div className={styles.arrowIcon} onClick={handleInputClick}>
          <DownArrow show={isOpen} />
        </div>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.optionsList}>
            {filteredOptions.length === 0 && (
              <div className={styles.noResults}>No countries found</div>
            )}
            {optionGroups
              ? optionGroups.map((group) => (
                  <React.Fragment key={group.key}>
                    <div className={styles.regionTitle}>{group.label}</div>
                    {group.countries.map((option) => (
                      <div
                        key={option.value}
                        className={classNames(styles.option, {
                          [styles.selected]: value?.value === option.value,
                        })}
                        onClick={() => handleSelect(option)}
                      >
                        <img
                          src={option.icon}
                          alt={option.label}
                          className={styles.flag}
                        />
                        <span className={styles.optionLabel}>
                          {option.label} ({option.value})
                        </span>
                      </div>
                    ))}
                  </React.Fragment>
                ))
              : filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={classNames(styles.option, {
                      [styles.selected]: value?.value === option.value,
                    })}
                    onClick={() => handleSelect(option)}
                  >
                    <img
                      src={option.icon}
                      alt={option.label}
                      className={styles.flag}
                    />
                    <span className={styles.optionLabel}>
                      {option.label} ({option.value})
                    </span>
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleCountrySelect;
